import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useCart } from '../../../context/CartContext';
import { formatCurrency } from '../../../utils/currency';
import { useTranslation } from 'react-i18next';

// Categories, languages, and filters are localized via i18n

const bestSellers = [
  { id: 1, image: '/assets/imgs/Home/Newest Comic Books/1.png', num: 1, price: 19.99 },
  { id: 2, image: '/assets/imgs/Home/Newest Comic Books/2.png', num: 2, price: 24.5 },
];

const products = Array.from({ length: 27 }).map((_, i) => ({
  id: i + 1,
  image: `/assets/imgs/Home/Newest Comic Books/${(i % 3) + 1}.png`,
  num: (i % 9) + 1,
  price: 14.99 + (i % 6) * 5,
  format: i % 2 === 0 ? 'E-Book' : 'Paper Book',
}));

const IconBox = ({ children }) => (
  <span className="inline-flex items-center justify-center w-5 h-5 rounded bg-brown text-cream mr-2">
    {children}
  </span>
);

const ShopSection = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation('common');
  const lng = (i18n.language || 'en').split('-')[0];
  const titleBase = {
    ar: 'اسم الكتاب', ur: 'کتاب کا نام', en: 'Book Name', fr: 'Nom du livre', de: 'Buchtitel', es: 'Nombre del libro', pt: 'Nome do livro', ja: '書名', hi: 'पुस्तक का नाम', it: 'Nome del libro', ru: 'Название книги', zh: '书名'
  }[lng] || 'Book Name';

  // Languages filter labels localization map by English key
  const langLabelMap = {
    ar: { Arabic: 'العربية', Chinese: 'الصينية', English: 'الإنجليزية', French: 'الفرنسية', German: 'الألمانية', Hindi: 'الهندية', Italian: 'الإيطالية', Japanese: 'اليابانية', Portuguese: 'البرتغالية', Russian: 'الروسية', Spanish: 'الإسبانية', Urdu: 'الأوردية' },
    ur: { Arabic: 'العربية', Chinese: 'چینی', English: 'انگریزی', French: 'فرانسیسی', German: 'جرمن', Hindi: 'ہندی', Italian: 'اطالوی', Japanese: 'جاپانی', Portuguese: 'پرتگالی', Russian: 'روسی', Spanish: 'ہسپانوی', Urdu: 'اردو' },
    en: { Arabic: 'Arabic', Chinese: 'Chinese', English: 'English', French: 'French', German: 'German', Hindi: 'Hindi', Italian: 'Italian', Japanese: 'Japanese', Portuguese: 'Portuguese', Russian: 'Russian', Spanish: 'Spanish', Urdu: 'Urdu' },
    fr: { Arabic: 'Arabe', Chinese: 'Chinois', English: 'Anglais', French: 'Français', German: 'Allemand', Hindi: 'Hindi', Italian: 'Italien', Japanese: 'Japonais', Portuguese: 'Portugais', Russian: 'Russe', Spanish: 'Espagnol', Urdu: 'Ourdou' },
    de: { Arabic: 'Arabisch', Chinese: 'Chinesisch', English: 'Englisch', French: 'Französisch', German: 'Deutsch', Hindi: 'Hindi', Italian: 'Italienisch', Japanese: 'Japanisch', Portuguese: 'Portugiesisch', Russian: 'Russisch', Spanish: 'Spanisch', Urdu: 'Urdu' },
    es: { Arabic: 'Árabe', Chinese: 'Chino', English: 'Inglés', French: 'Francés', German: 'Alemán', Hindi: 'Hindi', Italian: 'Italiano', Japanese: 'Japonés', Portuguese: 'Portugués', Russian: 'Ruso', Spanish: 'Español', Urdu: 'Urdú' },
    pt: { Arabic: 'Árabe', Chinese: 'Chinês', English: 'Inglês', French: 'Francês', German: 'Alemão', Hindi: 'Hindi', Italian: 'Italiano', Japanese: 'Japonês', Portuguese: 'Português', Russian: 'Russo', Spanish: 'Espanhol', Urdu: 'Urdu' },
    ja: { Arabic: 'アラビア語', Chinese: '中国語', English: '英語', French: 'フランス語', German: 'ドイツ語', Hindi: 'ヒンディー語', Italian: 'イタリア語', Japanese: '日本語', Portuguese: 'ポルトガル語', Russian: 'ロシア語', Spanish: 'スペイン語', Urdu: 'ウルドゥー語' },
    hi: { Arabic: 'अरबी', Chinese: 'चीनी', English: 'अंग्रेज़ी', French: 'फ़्रेंच', German: 'जर्मन', Hindi: 'हिंदी', Italian: 'इतालवी', Japanese: 'जापानी', Portuguese: 'पुर्तगाली', Russian: 'रूसी', Spanish: 'स्पैनिश', Urdu: 'उर्दू' },
    it: { Arabic: 'Arabo', Chinese: 'Cinese', English: 'Inglese', French: 'Francese', German: 'Tedesco', Hindi: 'Hindi', Italian: 'Italiano', Japanese: 'Giapponese', Portuguese: 'Portoghese', Russian: 'Russo', Spanish: 'Spagnolo', Urdu: 'Urdu' },
    ru: { Arabic: 'Арабский', Chinese: 'Китайский', English: 'Английский', French: 'Французский', German: 'Немецкий', Hindi: 'Хинди', Italian: 'Итальянский', Japanese: 'Японский', Portuguese: 'Португальский', Russian: 'Русский', Spanish: 'Испанский', Urdu: 'Урду' },
    zh: { Arabic: '阿拉伯语', Chinese: '中文', English: '英语', French: '法语', German: '德语', Hindi: '印地语', Italian: '意大利语', Japanese: '日语', Portuguese: '葡萄牙语', Russian: '俄语', Spanish: '西班牙语', Urdu: '乌尔都语' },
  }[lng] || {};

  // Categories from i18n or localized defaults if missing
  const categoriesRaw = t('shop.categories', { returnObjects: true }) || [];
  const defaultCategories = {
    ar: ['مغامرة','خيال علمي','فانتازيا','غموض / محقق','رعب','رومانسية','تاريخي','ترفيه'],
    ur: ['ایکشن / ایڈونچر','سائنس فکشن','فینٹسی','سسپنس / ڈیٹیکٹو','ہorror','رومانس','تاریخی','تفریح'],
    en: ['Action / Adventure','Science fiction','Fantasy','Mystery / Detective','Horror','Romance','Historical','Entertainment'],
    fr: ['Action / Aventure','Science-fiction','Fantastique','Mystère / Détective','Horreur','Romance','Historique','Divertissement'],
    de: ['Action / Abenteuer','Science-Fiction','Fantasy','Mystery / Detektiv','Horror','Romanze','Historisch','Unterhaltung'],
    es: ['Acción / Aventura','Ciencia ficción','Fantasía','Misterio / Detective','Terror','Romance','Histórico','Entretenimiento'],
    pt: ['Ação / Aventura','Ficção científica','Fantasia','Mistério / Detetive','Terror','Romance','Histórico','Entretenimento'],
    ja: ['アクション / アドベンチャー','SF','ファンタジー','ミステリー / 探偵','ホラー','ロマンス','歴史','エンターテインメント'],
    hi: ['एक्शन / एडवेंचर','विज्ञान कथा','फैंटेसी','रहस्य / जासूसी','हॉरर','रोमांस','ऐतिहासिक','मनोरंजन'],
    it: ['Azione / Avventura','Fantascienza','Fantasy','Mistero / Detective','Horror','Romantico','Storico','Intrattenimento'],
    ru: ['Боевик / Приключения','Научная фантастика','Фэнтези','Детектив','Ужасы','Романтика','Исторический','Развлечения'],
    zh: ['动作 / 冒险','科幻','奇幻','悬疑 / 侦探','恐怖','爱情','历史','娱乐'],
  }[lng] || [];
  const hasLocalCategories = Array.isArray(i18n.getResource(lng, 'common', 'shop.categories'));
  const categories = hasLocalCategories && categoriesRaw.length ? categoriesRaw : defaultCategories;

  const languagesRaw = t('shop.languages', { returnObjects: true }) || [];
  const languages = languagesRaw.map(l => ({ ...l, label: langLabelMap[l.label] || l.label }));

  const moreFiltersRaw = t('shop.moreFilters', { returnObjects: true }) || [];
  const defaultMoreFilters = {
    ar: ['كتاب إلكتروني', 'كتاب صوتي', 'كتاب ورقي'],
    ur: ['ای-بک', 'آڈیو بک', 'پرنٹ بک'],
    en: ['E-Book', 'Audio Book', 'Paper Book'],
    fr: ['Livre numérique', 'Livre audio', 'Livre papier'],
    de: ['E-Book', 'Hörbuch', 'Taschenbuch'],
    es: ['Libro electrónico', 'Audiolibro', 'Libro en papel'],
    pt: ['E-book', 'Audiolivro', 'Livro impresso'],
    ja: ['電子書籍', 'オーディオブック', '紙の本'],
    hi: ['ई-बुक', 'ऑडियो बुक', 'पेपर बुक'],
    it: ['E-book', 'Audiolibro', 'Libro cartaceo'],
    ru: ['Электронная книга', 'Аудиокнига', 'Печатная книга'],
    zh: ['电子书', '有声书', '纸质书']
  }[lng] || ['E-Book', 'Audio Book', 'Paper Book'];
  const hasLocalMore = Array.isArray(i18n.getResource(lng, 'common', 'shop.moreFilters'));
  const moreFilters = hasLocalMore && moreFiltersRaw.length ? moreFiltersRaw : defaultMoreFilters;
  const { addToCart } = useCart();
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 9;
  const totalPages = Math.ceil(products.length / itemsPerPage);
  const fmtNum = new Intl.NumberFormat(lng);

  const handleAdd = (p) => {
    const localizedTitle = `${titleBase} ${String(p.num).padStart(2,'0')}`;
    addToCart({ id: p.id, title: localizedTitle, image: p.image, price: p.price });
  };

  const handleBuyNow = (p) => {
    handleAdd(p);
    navigate('/checkout');
  };

  return (
    <section className="bg-cream py-16 lg:py-20">
      <div className="container mx-auto px-8 lg:px-16">
        <div className="text-center mb-12">
          <h2 className="text-4xl lg:text-5xl font-bold text-text-dark mb-2">{t('home.comicBooks.title')}</h2>
          <p className="text-base font-medium text-text-dark">{t('home.comicBooks.featured')}</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[280px_minmax(0,1fr)] gap-8">
          <aside className="space-y-6">
            <div className="bg-cream-dark border border-brown/20 rounded">
              <div className="px-4 py-3 border-b border-brown/20 flex items-center font-semibold text-text-dark">
                <IconBox>
                  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M2 12h20"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>
                </IconBox>
                {t('shop.filters.language')}
              </div>
              <ul className="p-4 space-y-2 text-sm">
                {[...languages].map(l => (
                  <li key={l.label} className="flex items-center text-text-dark/90"><input type="checkbox" className="mr-2 accent-brown"/> {l.label} <span className="ml-auto text-text-gray">({fmtNum.format(l.count)})</span></li>
                ))}
              </ul>
            </div>
            <div className="bg-cream-dark border border-brown/20 rounded">
              <div className="px-4 py-3 border-b border-brown/20 flex items-center font-semibold text-text-dark">
                <IconBox>
                  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18"/><path d="M8 12h13"/><path d="M13 18h8"/></svg>
                </IconBox>
                {t('shop.filters.category')}
              </div>
              <ul className="p-4 space-y-2 text-sm">
                {categories.map((c) => (
                  <li key={c} className="flex items-center text-text-dark/90">
                    <input type="checkbox" className="mr-2 accent-brown"/> {c}
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-cream-dark border border-brown/20 rounded">
              <div className="px-4 py-3 border-b border-brown/20 flex items-center font-semibold text-text-dark">
                <IconBox>
                  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg>
                </IconBox>
                {t('shop.filters.availability')}
              </div>
              <ul className="p-4 space-y-2 text-sm">
                <li className="flex items-center text-text-dark/90"><input type="checkbox" className="mr-2 accent-brown"/> {t('shop.filters.inStock', { count: 90 })}</li>
                <li className="flex items-center text-text-dark/90"><input type="checkbox" className="mr-2 accent-brown"/> {t('shop.filters.outOfStock', { count: 3 })}</li>
              </ul>
            </div>

            <div className="bg-cream-dark border border-brown/20 rounded">
              <div className="px-4 py-3 border-b border-brown/20 flex items-center font-semibold text-text-dark">
                <IconBox>
                  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7"/><path d="M14 3h7v7h-7z"/><path d="M3 14h7v7H3z"/><path d="M14 14h7v7h-7z"/></svg>
                </IconBox>
                {t('shop.filters.more')}
              </div>
              <ul className="p-4 space-y-2 text-sm">
                {moreFilters.map(m => (
                  <li key={m} className="flex items-center text-text-dark/90"><input type="checkbox" className="mr-2 accent-brown"/> {m}</li>
                ))}
              </ul>
            </div>

            <div className="bg-cream-dark border border-brown/20 rounded">
              <div className="px-4 py-3 border-b border-brown/20 flex items-center font-semibold text-text-dark">
                <IconBox>
                  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 12l2-2 4 4 10-10 2 2-12 12-6-6z"/></svg>
                </IconBox>
                {t('shop.filters.bestSeller')}
              </div>
              <div className="p-4 space-y-4">
                {bestSellers.map(b => (
                  <div key={b.id} className="flex items-center gap-3">
                    <div className="w-16 h-20 bg-[#e8dcc8] border-2 border-text-dark rounded overflow-hidden shrink-0">
                      <img src={b.image} alt={`${titleBase} ${String(b.num).padStart(2,'0')}`} className="w-full h-full object-cover" />
                    </div>
                    <div>
                      <div className="text-sm font-medium text-text-dark">{`${titleBase} ${String(b.num).padStart(2,'0')}`}</div>
                      <div className="text-sm font-bold text-price-red">{formatCurrency(b.price)}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </aside>

          <main>
            <div className="flex items-center justify-between mb-6 border border-[#cdbfb2] py-3 px-4">
              <div className="flex items-center gap-8">
                <div className="text-sm font-medium text-text-dark">{t('shop.sortBy')} ▾</div>
                <div className="text-sm font-medium text-text-dark">{t('shop.productsCount', { count: 100 })}</div>
              </div>
              <div className="flex items-center gap-2 text-text-dark">
                <button className="p-2 rounded border border-brown/40 text-brown hover:bg-cream-dark" aria-label="Grid">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg>
                </button>
                <button className="p-2 rounded border border-brown/40 text-brown hover:bg-cream-dark" aria-label="List">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M8 6h13"/><path d="M8 12h13"/><path d="M8 18h13"/><path d="M3 6h.01"/><path d="M3 12h.01"/><path d="M3 18h.01"/></svg>
                </button>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-6">
              {products.slice((currentPage-1)*itemsPerPage, currentPage*itemsPerPage).map((p) => (
                <div key={p.id} className="group">
                  <Link to="/product" className="block">
                  <div className="relative bg-[#e8dcc8] border-2 border-text-dark rounded overflow-hidden aspect-[3/4] mb-3 transition-all duration-500 group-hover:-translate-y-2 group-hover:scale-105 group-hover:shadow-xl">
                    {p.format === 'E-Book' ? (
                      <div className="absolute top-2 left-2 z-10">
                        <span className="px-2 py-1 text-[10px] font-bold tracking-wide uppercase bg-price-red text-white rounded">{t('shop.badges.eBook')}</span>
                      </div>
                    ) : (
                      <div className="absolute top-2 left-2 z-10">
                        <span className="px-2 py-1 text-[10px] font-semibold uppercase bg-brown text-cream rounded">{t('shop.badges.paperBook')}</span>
                      </div>
                    )}
                    <img src={p.image} alt={`${titleBase} ${String(p.num).padStart(2,'0')}`} className="w-full h-full object-cover" />
                    {/* Hover actions at bottom */}
                    <div className="absolute inset-0 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity pointer-events-none">
                      <div className="absolute inset-x-0 bottom-0 p-2 flex justify-center gap-1.5 md:gap-2">
                        <button onClick={(e) => { e.preventDefault(); handleBuyNow(p); }} className="px-3 py-1.5 md:px-4 md:py-2 bg-brown text-cream rounded-md text-[11px] md:text-xs font-medium tracking-wide shadow hover:bg-brown-dark pointer-events-auto">{t('shop.actions.buyNow')}</button>
                        <button onClick={(e) => { e.preventDefault(); handleAdd(p); }} className="px-3 py-1.5 md:px-4 md:py-2 bg-gold text-white rounded-md text-[11px] md:text-xs font-medium tracking-wide shadow hover:bg-yellow-600 pointer-events-auto">{t('shop.actions.addToCart')}</button>
                      </div>
                    </div>
                  </div>
                  </Link>
                  <div className="flex items-center justify-between mb-1">
                    <h5 className="text-base font-medium text-text-dark">{`${titleBase} ${String(p.num).padStart(2,'0')}`}</h5>
                    <div className="flex gap-0.5">
                      {[...Array(5)].map((_, i) => (
                        <svg key={i} xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="#ffcb36"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>
                      ))}
                    </div>
                  </div>
                  <div className="text-xs font-medium text-text-gray">{p.format === 'E-Book' ? t('shop.badges.eBook') : t('shop.badges.paperBook')}</div>
                  <p className="text-base font-bold text-price-red">{formatCurrency(p.price)}</p>
                </div>
              ))}
            </div>

            <div className="flex items-center justify-center gap-4 mt-10">
              {/* prev */}
              <button onClick={() => setCurrentPage(p => Math.max(1, p-1))} className="w-11 h-11 rounded-full border border-[#cdbfb2] flex items-center justify-center text-[#151716] disabled:opacity-50" aria-label="Prev" disabled={currentPage===1}>
                &lt;
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(n => (
                <button
                  key={n}
                  className="w-11 h-11 rounded-full border border-[#cdbfb2] flex items-center justify-center text-sm font-medium"
                  style={n===currentPage ? { backgroundColor: '#6c534e', color: '#fefefe' } : { color: '#151716' }}
                  onClick={() => setCurrentPage(n)}
                >
                  {String(n).padStart(2,'0')}
                </button>
              ))}
              {/* next */}
              <button onClick={() => setCurrentPage(p => Math.min(totalPages, p+1))} className="w-11 h-11 rounded-full border border-[#cdbfb2] flex items-center justify-center text-[#151716] disabled:opacity-50" aria-label="Next" disabled={currentPage===totalPages}>
                &gt;
              </button>
            </div>
          </main>
        </div>
      </div>
    </section>
  );
};

export default ShopSection;
